import React, { useState, useEffect, useCallback } from 'react';
import { Avatar, Button, Modal, Spin, Typography, Tag, Row, Col, Space, theme } from 'antd';
import {
  BarChartOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  FireOutlined,
  RobotOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useIntl } from '@umijs/max';
import { message } from '@/utils/globalMessage';
import { getUserInfo } from '@/services/ant-design-pro/api';
import dayjs from 'dayjs';
import { normalizeLocale } from '@/utils/locale';

const { Text } = Typography;
const { useToken } = theme;

interface UserInfo {
  userid: number;
  username: string;
  email: string;
  avatar: string;
  role: string;
  accountType?: 'parent' | 'staff';
  uvQuota: number;
  uvUsed: number;
  uvRemaining: number;
  aiCloneRemaining: number;
  expiresAt?: string | null;
}

interface UVStatusModalProps {
  visible: boolean;
  onCancel: () => void;
}

const UVStatusModal: React.FC<UVStatusModalProps> = ({ visible, onCancel }) => {
  const intl = useIntl();
  const locale = normalizeLocale(intl.locale);
  const tx = (zh: string, en: string, ja: string) => {
    if (locale === 'ja-JP') return ja;
    if (locale === 'en-US') return en;
    return zh;
  };
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const { token } = useToken();

  // 检测是否为深色模式
  const isDarkMode = token.colorBgBase === '#000000' || token.colorBgContainer === '#141414';

  // 获取用户信息
  const fetchUserInfo = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getUserInfo();
      if (response.data) {
        console.log('获取用户信息成功:', response.data);
        setUserInfo(response.data);
      } else {
        message.error(
          tx(
            '获取用户信息失败，请稍后重试',
            'Failed to load user info. Please try again later.',
            'ユーザー情報の取得に失敗しました。しばらくしてから再試行してください。',
          ),
        );
      }
    } catch (error: any) {
      console.error('获取用户信息失败:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        tx(
          '获取用户信息失败，请稍后重试',
          'Failed to load user info. Please try again later.',
          'ユーザー情報の取得に失敗しました。しばらくしてから再試行してください。',
        );
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [locale]);

  // 弹窗打开时获取数据，关闭时清空状态
  useEffect(() => {
    if (visible) {
      fetchUserInfo();
    } else {
      setUserInfo(null);
    }
  }, [visible, fetchUserInfo]);

  // 计算到期天数
  const getDaysUntilExpiry = (expiresAt: string | null | undefined): number | null => {
    if (!expiresAt || expiresAt === '') {
      return null;
    }
    const expiryDate = dayjs(expiresAt);
    if (!expiryDate.isValid()) {
      return null;
    }
    const now = dayjs();
    const days = expiryDate.diff(now, 'day');
    return days;
  };

  // 格式化到期时间
  const formatExpiresAt = (expiresAt: string | null | undefined): string => {
    if (!expiresAt) {
      return tx('永久有效', 'Permanent', '無期限');
    }
    const expiryDate = dayjs(expiresAt);
    if (!expiryDate.isValid()) {
      return tx('永久有效', 'Permanent', '無期限');
    }
    return expiryDate.format('YYYY-MM-DD HH:mm:ss');
  };

  const handleCopyAccountInfo = async () => {
    if (!userInfo) return;

    const accountInfo = `${tx('用户', 'User', 'ユーザー')}: ${userInfo.username}\n${tx('邮箱', 'Email', 'メール')}: ${userInfo.email}`;

    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(accountInfo);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = accountInfo;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!copied) throw new Error('Copy failed');
      }
      message.success(
        tx(
          '账号和邮箱已复制',
          'Account and email copied',
          'アカウントとメールアドレスをコピーしました',
        ),
      );
    } catch {
      message.error(
        tx(
          '复制失败，请手动复制',
          'Copy failed. Please copy manually.',
          'コピーに失敗しました。手動でコピーしてください。',
        ),
      );
    }
  };

  // 获取UV使用率颜色
  const getUVColor = (remaining: number, quota: number): string => {
    if (quota <= 0) return remaining <= 0 ? '#ff4d4f' : '#52c41a';
    const percentage = (remaining / quota) * 100;
    if (percentage <= 10) return '#ff4d4f';
    if (percentage <= 30) return '#faad14';
    return '#52c41a';
  };

  // 获取到期天数标签
  const getExpiryTag = (days: number | null) => {
    const tagStyle = { borderRadius: 999, fontSize: 12, marginInlineEnd: 0, paddingInline: 9 };
    if (days === null) {
      return <Tag color="success" style={tagStyle}>{tx('永久有效', 'Permanent', '無期限')}</Tag>;
    }
    if (days < 0) {
      return <Tag color="error" style={tagStyle}>{tx('已过期', 'Expired', '期限切れ')}</Tag>;
    }
    if (days <= 3) {
      return (
        <Tag color="error" style={tagStyle}>
          {tx(`剩余 ${days} 天`, `${days} days left`, `残り ${days} 日`)}
        </Tag>
      );
    }
    if (days <= 7) {
      return (
        <Tag color="warning" style={tagStyle}>
          {tx(`剩余 ${days} 天`, `${days} days left`, `残り ${days} 日`)}
        </Tag>
      );
    }
    return (
      <Tag color="success" style={tagStyle}>
        {tx(`剩余 ${days} 天`, `${days} days left`, `残り ${days} 日`)}
      </Tag>
    );
  };

  const uvQuota = userInfo?.uvQuota ?? 0;
  const uvUsed = userInfo?.uvUsed ?? 0;
  const uvRemaining = userInfo?.uvRemaining ?? 0;
  const aiCloneRemaining = userInfo?.aiCloneRemaining ?? 0;
  const expiresAt = userInfo?.expiresAt ?? null;
  const daysUntilExpiry = getDaysUntilExpiry(expiresAt);

  const metricItems = [
    {
      key: 'remaining-uv',
      title: tx('剩余 UV', 'Remaining UV', '残り UV'),
      value: uvRemaining.toLocaleString(),
      icon: <ThunderboltOutlined />,
      color: getUVColor(uvRemaining, uvQuota),
      background: isDarkMode
        ? 'linear-gradient(145deg, rgba(82,196,26,0.16), rgba(82,196,26,0.05))'
        : 'linear-gradient(145deg, #f6ffed 0%, #ffffff 78%)',
      borderColor: isDarkMode ? 'rgba(82,196,26,0.28)' : '#d9f7be',
    },
    {
      key: 'used-uv',
      title: tx('已使用 UV', 'Used UV', '使用済み UV'),
      value: uvUsed.toLocaleString(),
      icon: <FireOutlined />,
      color: '#fa8c16',
      background: isDarkMode
        ? 'linear-gradient(145deg, rgba(250,140,22,0.16), rgba(250,140,22,0.05))'
        : 'linear-gradient(145deg, #fff7e6 0%, #ffffff 78%)',
      borderColor: isDarkMode ? 'rgba(250,140,22,0.28)' : '#ffe7ba',
    },
    {
      key: 'remaining-ai',
      title: tx('剩余 AI 点数', 'Remaining AI Credits', '残り AI クレジット'),
      value: aiCloneRemaining === -1
        ? tx('不限', 'Unlimited', '無制限')
        : aiCloneRemaining.toLocaleString(),
      icon: <RobotOutlined />,
      color: '#722ed1',
      background: isDarkMode
        ? 'linear-gradient(145deg, rgba(114,46,209,0.2), rgba(114,46,209,0.06))'
        : 'linear-gradient(145deg, #f9f0ff 0%, #ffffff 78%)',
      borderColor: isDarkMode ? 'rgba(114,46,209,0.34)' : '#efdbff',
    },
  ];

  // 深色模式和浅色模式的样式
  const styles = {
    userCard: {
      padding: '22px 24px',
      background: isDarkMode
        ? 'linear-gradient(135deg, #26213b 0%, #192230 100%)'
        : 'linear-gradient(135deg, #202a44 0%, #394a6d 58%, #51406e 100%)',
      borderRadius: 16,
      marginBottom: 22,
      boxShadow: isDarkMode ? 'none' : '0 14px 34px rgba(32, 42, 68, 0.18)',
    }
  };

  return (
    <Modal
      title={(
        <Space size={10}>
          <span
            style={{
              alignItems: 'center',
              background: 'linear-gradient(135deg, #1677ff, #722ed1)',
              borderRadius: 9,
              color: '#fff',
              display: 'inline-flex',
              height: 30,
              justifyContent: 'center',
              width: 30,
            }}
          >
            <BarChartOutlined />
          </span>
          <span>{tx('使用查询', 'Usage Lookup', '利用照会')}</span>
        </Space>
      )}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={680}
      styles={{ body: { paddingTop: 12 } }}
    >
      <Spin spinning={loading || !userInfo}>
        {userInfo ? (
          <div>
            {/* 用户信息头部 */}
            <div style={styles.userCard}>
              <Row align="middle" gutter={[18, 16]}>
                <Col xs={24} sm={14}>
                  <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0 }}>
                    <Avatar
                      size={46}
                      src={userInfo.avatar || undefined}
                      style={{
                        background: 'linear-gradient(135deg, #69b1ff, #b37feb)',
                        border: '2px solid rgba(255,255,255,0.34)',
                        boxShadow: '0 6px 18px rgba(0,0,0,0.2)',
                        flex: '0 0 auto',
                        fontSize: 19,
                        fontWeight: 700,
                      }}
                    >
                      {userInfo.username?.slice(0, 1).toUpperCase()}
                    </Avatar>
                    <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0 }}>
                      <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                        <div
                          style={{
                            alignItems: 'baseline',
                            display: 'flex',
                            fontSize: 14,
                            gap: 5,
                            overflow: 'hidden',
                          }}
                        >
                          <span style={{ color: 'rgba(255,255,255,0.62)', flex: '0 0 auto' }}>
                            {tx('用户：', 'User:', 'ユーザー：')}
                          </span>
                          <strong style={{ color: '#fff', fontSize: 17, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {userInfo.username}
                          </strong>
                        </div>
                        <div
                          style={{
                            alignItems: 'baseline',
                            display: 'flex',
                            fontSize: 13,
                            gap: 5,
                            minWidth: 0,
                          }}
                        >
                          <span style={{ color: 'rgba(255,255,255,0.62)', flex: '0 0 auto' }}>
                            {tx('邮箱：', 'Email:', 'メール：')}
                          </span>
                          <span style={{ color: 'rgba(255,255,255,0.82)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {userInfo.email}
                          </span>
                          {userInfo.accountType === 'staff' && (
                            <span style={{ color: 'rgba(255,255,255,0.62)', flex: '0 0 auto', fontSize: 12 }}>
                              {tx('员工', 'Staff', '従業員')}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        icon={<CopyOutlined />}
                        onClick={handleCopyAccountInfo}
                        size="small"
                        style={{
                          background: 'rgba(255,255,255,0.1)',
                          borderColor: 'rgba(255,255,255,0.2)',
                          borderRadius: 7,
                          color: '#fff',
                          flex: '0 0 auto',
                          fontSize: 12,
                        }}
                      >
                        {tx('复制', 'Copy', 'コピー')}
                      </Button>
                    </div>
                  </div>
                </Col>
                <Col xs={24} sm={10}>
                  <div
                    style={{
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.13)',
                      borderRadius: 12,
                      color: '#fff',
                      padding: '13px 15px',
                    }}
                  >
                    <div
                      style={{
                        alignItems: 'center',
                        color: 'rgba(255,255,255,0.68)',
                        display: 'flex',
                        fontSize: 12,
                        gap: 7,
                      }}
                    >
                      <ClockCircleOutlined />
                      {tx('会员到期时间', 'Membership expiry', '会員有効期限')}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 650, margin: '6px 0 9px', whiteSpace: 'nowrap' }}>
                      {formatExpiresAt(expiresAt)}
                    </div>
                    {getExpiryTag(daysUntilExpiry)}
                  </div>
                </Col>
              </Row>
            </div>

            <div
              style={{
                marginBottom: 10,
              }}
            >
              <Text strong style={{ fontSize: 15 }}>
                {tx('用量概览', 'Usage overview', '利用状況')}
              </Text>
            </div>

            {/* 用量统计区域 */}
            <Row gutter={[12, 12]}>
              {metricItems.map((item) => (
                <Col key={item.key} xs={24} sm={8}>
                  <div
                    style={{
                      background: item.background,
                      border: `1px solid ${item.borderColor}`,
                      borderRadius: 14,
                      boxShadow: isDarkMode ? 'none' : '0 7px 18px rgba(31, 35, 48, 0.06)',
                      height: '100%',
                      minHeight: 124,
                      padding: '16px 17px',
                    }}
                  >
                    <div
                      style={{
                        alignItems: 'center',
                        background: `${item.color}18`,
                        borderRadius: 10,
                        color: item.color,
                        display: 'inline-flex',
                        fontSize: 18,
                        height: 36,
                        justifyContent: 'center',
                        marginBottom: 12,
                        width: 36,
                      }}
                    >
                      {item.icon}
                    </div>
                    <div style={{ color: token.colorTextSecondary, fontSize: 13, marginBottom: 3 }}>
                      {item.title}
                    </div>
                    <div
                      style={{
                        color: item.color,
                        fontSize: item.key === 'remaining-ai' ? 18 : 19,
                        fontWeight: 750,
                        letterSpacing: '-0.5px',
                        lineHeight: 1.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={item.value}
                    >
                      {item.value}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>

          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Text type="secondary">{tx('加载中...', 'Loading...', '読み込み中...')}</Text>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default UVStatusModal;
